import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import { isMysteryShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import useDataList from 'utils/hooks/useDataList';

import {
  SHOP_REQUEST_COLUMNS,
  SHOP_REQUEST_DUE_DATE_RANGES,
  SHOP_REQUEST_VIEW_GROUP_BY,
  SHOP_REQUESTS_PAGE_SIZE,
} from './constants';
import ShopRequestFilters from './Filters/ShopRequestFilters';
import ShopRequestsCreateButton from './ShopRequestsCreateButton';
import { ShopRequestsDataContext } from './ShopRequestsDataContext';
import ShopRequestSearch from './ShopRequestSearch';
import ShopRequestsGroupTable from './ShopRequestsTable/ShopRequestsGroupTable';
import ShopRequestsTable from './ShopRequestsTable/ShopRequestsTable';
import ShopRequestTableSelectorControl from './ShopRequestsTable/ShopRequestTableSelectorControl';
import ShopRequestsTabs from './Tabs/ShopRequestsTabs';

interface Props {
  onSelectionChanged: any;
}

const SearchAndCreateContainer = styled.div`
  margin: 0 30px;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
`;

const Container = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
  margin-top: 12px;
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ShopRequestsData = ({ onSelectionChanged }: Props) => {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItemsIds, setSelectedItemsIds] = useState<string[]>([]);
  const [totalItemsIds, setTotalItemsIds] = useState<string[]>([]);
  const [openEditBatchShopRequestsModel, setOpenEditBatchShopRequestsModel] = useState<boolean>(false);

  const { currentUser } = useAppSelector((state) => state.auth);
  const isShopRequestAdmin = currentUser && isMysteryShopAdmin(currentUser);
  const shouldFilterForCurrentShopper = Boolean(currentUser && !currentUser.isSuperuser && !isShopRequestAdmin);
  const { items, filters, ordering, setOrdering, setFilters, setPage, setItems, setSearch } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: SHOP_REQUESTS_PAGE_SIZE,
        search: '',
        status: [],
        shoppers: [],
        auditors: [],
        companyIds: [],
        noPropagation: false,
        dueDate: undefined,
        dueDateRange: SHOP_REQUEST_DUE_DATE_RANGES.DUE_THIS_WEEK,
        groupBy: undefined,
      },
      ordering: SHOP_REQUEST_COLUMNS.dueDate.id,
    },
  });

  const handleSelection = (items: any[]) => {
    onSelectionChanged(items);
  };

  const resetSelection = () => {
    setSelectedItemsIds([]);
    setOpenEditBatchShopRequestsModel(false);
  };

  const showShopRequestGroupTable =
    filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.REQUEST || filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.COMMUNITY;

  useEffect(() => {
    if (currentUser) {
      setFilters({
        shoppers: shouldFilterForCurrentShopper ? [currentUser.PAUser_Id, 0] : [],
      });
    }
  }, [currentUser, setFilters, shouldFilterForCurrentShopper]);

  return (
    <ShopRequestsDataContext.Provider
      value={{
        items: items ?? [],
        filters,
        ordering,
        selectionMode,
        openEditBatchShopRequestsModel,
        selectedItemsIds,
        totalItemsIds,
        setTotalItemsIds,
        setSelectionMode,
        setOpenEditBatchShopRequestsModel,
        setSelectedItemsIds,
        setOrdering,
        setFilters,
        setSearch,
        setPage,
        setItems,
        handleSelection,
        resetSelection,
      }}
    >
      <Container>
        <SearchAndCreateContainer>
          <ShopRequestSearch />
          {isShopRequestAdmin && <ShopRequestsCreateButton />}
        </SearchAndCreateContainer>
        <ShopRequestFilters />
        <RowContainer>
          <ShopRequestsTabs />
          <ShopRequestTableSelectorControl onEditMultiple={() => setOpenEditBatchShopRequestsModel(true)} />
        </RowContainer>

        {showShopRequestGroupTable ? <ShopRequestsGroupTable /> : <ShopRequestsTable />}
      </Container>
    </ShopRequestsDataContext.Provider>
  );
};

export default ShopRequestsData;
